import { ClipboardCheck } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

export function ClaimsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const claims: Array<Record<string, unknown>> = Array.isArray(
    state.result?.claims
  )
    ? state.result.claims
    : []
  const count = Array.isArray(state.result?.claims)
    ? state.result.claims.length
    : null
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Listing claims...'
        completedLabel={
          count !== null ? `${plural(count, 'claim')} listed` : 'Claims listed'
        }
        completedDetail={pickString(
          state.args?.policySlug,
          state.args?.controlRef
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No claims found'
          items={claims.map((claim) => ({
            id: pickString(claim.id),
            title: pickString(claim.text) ?? 'Untitled claim',
            subtitle: pickString(claim.displayCode, claim.control),
            meta: [claim.type, claim.verification, claim.confidence],
            tone: normalizeTone(claim.confidence),
          }))}
        />
      ) : null}
    </>
  )
}

export function ClaimsFindToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const claim = state.result?.claim ?? state.result
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Finding claim...'
        completedLabel={
          state.result?.found === false ? 'Claim not found' : 'Claim found'
        }
        loadingDetail={pickString(state.args?.claimId, state.args?.policySlug)}
        completedDetail={pickString(
          state.args?.claimId,
          state.args?.policySlug
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) && claim ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `claim-${pickString(claim.id, state.args?.claimId) ?? 'detail'}`
          )}
          payload={{
            entityType: 'claim',
            toolName: 'claimsFind',
            title: pickString(claim.text) ?? 'Untitled claim',
            subtitle: pickString(claim.id, state.args?.claimId),
            description: pickString(claim.quote),
            fields: [
              { label: 'Type', value: formatToolValue(claim.type) },
              { label: 'Control', value: formatToolValue(claim.displayCode ?? claim.control) },
              {
                label: 'System of record',
                value: formatToolValue(claim.systemOfRecord),
              },
              {
                label: 'Verification',
                value: formatToolValue(claim.verification),
              },
              {
                label: 'Confidence',
                value: formatToolValue(claim.confidence),
                tone: normalizeTone(claim.confidence),
              },
              {
                label: 'Policy',
                value: pickString(
                  claim.policy?.title,
                  claim.policy?.slug,
                  state.args?.policySlug
                ),
              },
              { label: 'Block', value: formatToolValue(claim.blockId) },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function ClaimsRecordToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Recording claim...'
      completedLabel='Claim recorded'
      completedDetail={pickString(
        state.result?.controlRef,
        state.args?.controlRef,
        state.args?.policySlug
      )}
      completedIcon={completedIcon}
    />
  )
}

export function ClaimsEditToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating claim...'
      completedLabel='Claim updated'
      completedDetail={pickString(state.args?.claimId)}
      completedIcon={completedIcon}
    />
  )
}

export function ClaimsDeleteToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Deleting claim...'
      completedLabel='Claim deleted'
      completedDetail={pickString(state.args?.claimId)}
      completedIcon={completedIcon}
    />
  )
}
